// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE TRADE-SPECIFIC LANDING PAGE TEMPLATE
// Purpose: the base for future pages like /websites-for-plumbers or
// /websites-for-electricians. To create a new trade page:
//   1. Duplicate this file and give it a real route (e.g. /websites-for-plumbers).
//   2. Edit the CONFIG object — trade name, audience language, example jobs and
//      a genuinely relevant image. Each trade page MUST have distinct, useful
//      copy (do not just swap the trade name — Google ignores near-duplicates).
//   3. Remove noindex from the Seo component once the page is real.
// This template itself lives at /trade-template and is noindexed.
// ─────────────────────────────────────────────────────────────────────────────
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import { IMAGES, PACKAGES } from '../data/content';

const CONFIG = {
  trade: 'Plumber', // singular, e.g. 'Painter & Decorator'
  tradePlural: 'Plumbers',
  areaPhrase: 'in your area', // or a specific town for a genuinely local page
  exampleJobs: ['Emergency call-outs', 'Bathroom installations', 'Boiler and heating work'],
  image: IMAGES.plumbingHero,
};

export default function TradeLandingTemplate() {
  return (
    <>
      <Seo
        title={`Websites for ${CONFIG.tradePlural} — Template | Build My Trade`}
        description={`Professional websites, Google Business Profiles and social media setup for ${CONFIG.tradePlural.toLowerCase()} across the UK.`}
        path="/trade-template"
        noindex
      />
      <section className="bg-charcoal bg-blueprint" data-testid="trade-template-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              For {CONFIG.tradePlural.toLowerCase()} across the UK
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl">
              A website that wins work while you’re on the tools.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a {CONFIG.trade.toLowerCase()} needs to be found {CONFIG.areaPhrase}:
              a professional website, a complete Google Business Profile and branded social pages — for one fixed,
              published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="trade-template-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="trade-template-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
          </div>
          <Reveal>
            <img
              src={CONFIG.image.url}
              alt={CONFIG.image.alt}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-offwhite" data-testid="trade-template-detail">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl">
                What a {CONFIG.trade.toLowerCase()}’s website needs to do
              </h2>
              <ul className="mt-8 space-y-4">
                {CONFIG.exampleJobs.map((job, i) => (
                  <li key={i} className="rounded-xl border border-hairline bg-white p-5 text-sm font-medium text-charcoal">
                    {job}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <h2 className="text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl">
                Packages that fit a {CONFIG.trade.toLowerCase()}’s business
              </h2>
              <div className="mt-8 grid gap-6">
                {PACKAGES.map((pkg, i) => (
                  <PackageCard key={pkg.slug} pkg={pkg} delay={0.06 * i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
